import React, { useState, useEffect } from 'react';

/**
 * Composant de sélection des modalités de paiement
 * Charge les modalités depuis l'API et permet la personnalisation
 */
const PaymentTermsSelector = ({ value = '', onChange, className = '' }) => {
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [customText, setCustomText] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [loading, setLoading] = useState(true);

  // Charger les modalités depuis l'API
  useEffect(() => {
    const fetchPaymentTerms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment-terms`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPaymentTerms(data);
          
          // Trouver la modalité par défaut
          const defaultTerm = data.find(t => t.is_default);
          if (defaultTerm && !value) {
            setSelectedTermId(defaultTerm.id);
            onChange(defaultTerm.description);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des modalités:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentTerms();
  }, []);

  // Initialiser la sélection depuis la valeur existante
  useEffect(() => {
    if (value && paymentTerms.length > 0) {
      // Vérifier si la valeur correspond à une modalité existante
      const matchedTerm = paymentTerms.find(term => 
        term.description && value.includes(term.description.substring(0, 30))
      );
      
      if (matchedTerm) {
        setSelectedTermId(matchedTerm.id);
        setCustomText('');
      } else {
        setSelectedTermId('custom');
        setCustomText(value);
      }
    }
  }, [value, paymentTerms]);

  const handleTermChange = (e) => {
    const termId = e.target.value;
    setSelectedTermId(termId);
    
    if (termId === 'custom') {
      // Mode personnalisé
      setCustomText('');
      onChange('');
    } else {
      const selectedTerm = paymentTerms.find(t => t.id === termId);
      if (selectedTerm) {
        // Modalité prédéfinie
        onChange(selectedTerm.description);
      }
    }
  };

  const handleCustomTextChange = (e) => {
    const text = e.target.value;
    setCustomText(text);
    onChange(text);
  };

  const selectedTerm = paymentTerms.find(t => t.id === selectedTermId);
  const isCustom = selectedTermId === 'custom';

  if (loading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Modalités de paiement
        </label>
        <div className="animate-pulse bg-gray-200 h-10 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Label et dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Modalités de paiement
        </label>
        <select
          value={selectedTermId}
          onChange={handleTermChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
        >
          <option value="">-- Sélectionner une modalité --</option>
          {paymentTerms.map((term) => (
            <option key={term.id} value={term.id}>
              {term.name} {term.is_default ? '⭐' : ''} {term.payment_delay_days > 0 ? `(${term.payment_delay_days}j)` : ''}
            </option>
          ))}
          <option value="custom">✏️ Personnalisé</option>
        </select>
      </div>

      {/* Aperçu ou textarea personnalisé */}
      {isCustom ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {customText ? 'Modifier les conditions' : 'Nouvelles conditions de paiement'}
          </label>
          <textarea
            value={customText}
            onChange={handleCustomTextChange}
            placeholder="Saisissez vos conditions de paiement personnalisées..."
            rows={5}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
          />
        </div>
      ) : selectedTerm && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-medium text-green-600 uppercase tracking-wide">
              Aperçu
            </span>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              {showPreview ? 'Masquer' : 'Afficher'}
            </button>
          </div>
          
          {showPreview && (
            <>
              <h4 className="font-medium text-gray-800 mb-2">{selectedTerm.name}</h4>
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {selectedTerm.description}
              </p>
              {selectedTerm.discount_percentage > 0 && (
                <p className="text-xs text-orange-600 mt-2">
                  💰 {selectedTerm.discount_percentage}% de remise si paiement sous {selectedTerm.discount_days} jours
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Option pour modifier la modalité sélectionnée */}
      {selectedTerm && !isCustom && (
        <button
          type="button"
          onClick={() => {
            setSelectedTermId('custom');
            setCustomText(selectedTerm.description);
            onChange(selectedTerm.description);
          }}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <span>✏️</span>
          <span>Modifier cette modalité</span>
        </button>
      )}

      {/* Lien vers la gestion des modalités */}
      <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
        💡 Gérez vos modalités depuis <strong>Documents → Modalités paiement</strong>
      </div>
    </div>
  );
};

export default PaymentTermsSelector;
